import { Grid, Skeleton, FormLabel, FormGroup, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { t } from 'i18next';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { useGlobals } from 'src/shared/contexts/appContext';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { getUserAssociations, getUserDistricts } from 'src/shared/functions/organizationHelpers';
import { UserFlagOptions } from './userFlagOptions';

type ProfileGroupSectionProps = {
  loading: boolean;
  isEditable: boolean;
};

export const ProfileGroupSection = (props: ProfileGroupSectionProps) => {
  const { loading, isEditable } = props;
  const { currentUser } = useGlobals();
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const [association, setAssociation] = useState<Association>();
  const [district, setDistrict] = useState<District>();

  const hasSingleAssociation = getUserAssociations(currentUser);
  const hasSingleDistricts = getUserDistricts(currentUser);

  const userFlagOptions = UserFlagOptions;

  const watchedCountyAssId = watch('countyAss_id');
  const watchedLocalAssId = watch('localAss_id');

  return (
    <>
      <Grid item xs={12}>
        <ControlledTextField disabled={loading || !isEditable} fullWidth label={t('memberProfile:createProfile.name')} name='title' />
      </Grid>
      <Grid
        item
        xs={12}
        gap={2}
        sx={{
          py: 2,
          display: 'flex',
          justifyContent: 'flex-start',
          '& .MuiAutocomplete-inputRoot': {
            backgroundColor: 'white',
          },
        }}
      >
        {currentUser ? (
          <AssociationAndDistrictSelect
            disabled={!isEditable || hasSingleAssociation.length === 1}
            disableDistrict={hasSingleDistricts.length === 1}
            initialDistrictId={watchedCountyAssId}
            initialAssociationId={watchedLocalAssId}
            gridXs={12}
            gridLg={6}
            useGridLayout
            hideDistrict={currentUser?.districts.length === 0}
            association={association}
            district={district}
            associationCallback={(val) => {
              setAssociation(val || undefined);
              setValue('localAss_id', val?.localAss_id || 0);
            }}
            districtCallback={(val) => {
              setDistrict(val || undefined);
              setValue('countyAss_id', val?.countyAss_id || 0);
            }}
          />
        ) : (
          <Skeleton sx={{ width: '25vw' }} />
        )}
      </Grid>
      <Grid container mt={2} justifyContent='space-between'>
        <Grid item xs={12} md={6} sx={{ mb: { xs: 2, md: 0 } }}>
          <FormLabel id='label-userFlags'>{t('memberProfile:createProfile.profileAvailable')}</FormLabel>

          <FormGroup sx={{ ml: 1 }}>
            {userFlagOptions.map((item) => (
              <FormControlLabel
                disabled={!isEditable}
                key={item.value}
                control={
                  <Checkbox
                    // eslint-disable-next-line no-bitwise
                    checked={(watch('userFlags') & item.value) !== 0}
                    onChange={(e) => {
                      const current = watch('userFlags') || 0;
                      // eslint-disable-next-line no-bitwise
                      const updated = e.target.checked ? current | item.value : current & ~item.value;
                      setValue('userFlags', updated);
                    }}
                  />
                }
                label={t(item.label)}
              />
            ))}
          </FormGroup>
          {typeof errors.userFlags?.message === 'string' && (
            <Typography color='error' sx={{ mt: 1 }}>
              {errors.userFlags.message}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};
